import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { useAnalyticsOrgLocations, useScrollToTop } from '@frontend/analytics-shared';
import { Page } from '@frontend/page';
import { theme } from '@frontend/theme';
import { Heading } from '@frontend/design-system';
import { AppointmentAnalyticsBreadcrumb } from '../../atoms/appointment-analytics-breadcrumb';
import { useAppointmentAnalyticsV2ShallowStore } from '../../hooks/use-appointment-analytics-v2-store';
import { useIsAppointmentDemoAccount } from '../../hooks/use-is-appointment-demo-account';
import { AppointmentLocationFilter } from '../filters/appointment-location-filter';
import { ProspectPatientList } from '../upcoming-appointments/prospect-patient-list';

export const ProspectPatientListView = () => {
  const { t } = useTranslation('analytics');
  const isDemoAccount = useIsAppointmentDemoAccount();
  const { preselectedAccessibleLocationIds } = useAnalyticsOrgLocations({
    isDemoAccount,
    module: 'APPOINTMENT',
  });
  const { filterHintText } = useAppointmentAnalyticsV2ShallowStore('filterHintText');
  useScrollToTop();

  const [localLocationIds, setLocalLocationIds] = useState<string[] | undefined>();

  useEffect(() => {
    if (preselectedAccessibleLocationIds?.length && localLocationIds === undefined) {
      setLocalLocationIds(preselectedAccessibleLocationIds);
    }
  }, [preselectedAccessibleLocationIds, localLocationIds]);

  return (
    <Page>
      <Page.Header>
        <AppointmentAnalyticsBreadcrumb />
        <Page.Header.Heading>
          <Page.Header.Title
            title={
              <Heading level={1} css={{ display: 'inline-flex', alignItems: 'baseline', gap: theme.spacing(1) }}>
                {t('Prospect Patient List')}
              </Heading>
            }
          />
          <Page.Header.Subtitle
            subtitle={t('Showing results for {{filtersInfo}}.', {
              filtersInfo: filterHintText,
            })}
          />
        </Page.Header.Heading>
      </Page.Header>
      <Page.Body>
        <div css={styles.bodyContent}>
          <div css={styles.filtersWrapper}>
            <AppointmentLocationFilter value={localLocationIds} onChange={setLocalLocationIds} />
          </div>
          <ProspectPatientList
            pageSize={10}
            showHeader={false}
            showNumRowSelector={true}
            locationIdOverride={localLocationIds}
          />
        </div>
      </Page.Body>
    </Page>
  );
};

const styles = {
  bodyContent: css`
    padding-top: ${theme.spacing(3)};
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(3)};
  `,
  filtersWrapper: css`
    display: flex;
    gap: ${theme.spacing(2)};
    align-items: center;
  `,
};
